import { useState, type ReactNode } from 'react'

import type { CifrasDelRango, Periodo } from '@/lib/supabase/tipos-bd'
import { porcentaje } from '@/shared/utilidades/formato'

import { hoy } from '../semana'
import {
  abreviado,
  aumento,
  correrDia,
  cuantos,
  EL_ANTERIOR,
  EL_PERIODO,
  nombreDelPeriodo,
  rango,
} from './cifras'
import { CarteraDelPeriodo } from './CarteraDelPeriodo'
import { GraficaDeTramos } from './GraficaDeTramos'
import { PorTecnico } from './PorTecnico'
import { Rechazos } from './Rechazos'
import { TarjetaDeCifra } from './TarjetaDeCifra'
import { useElPeriodo } from './useElPeriodo'
import estilos from './CifrasDelPeriodo.module.css'

/**
 * LAS CIFRAS DEL PERIODO QUE SE QUIERA VER (0243, 0244).
 *
 * Johan: «en la pantalla de general quiero los dashboard: productividad
 * (servicios finalizados y valor), cotizaciones aceptadas, rechazadas…», y
 * después: «el aumento tiene que ser porcentual, respecto a lo que yo lo
 * quiera ver: semana a semana, por ejemplo», «cotizaciones hechas: solo las
 * de la app y la matriz, no las de materiales» y «le falta fuerza a esas
 * cards».
 *
 *   · Semana, mes o año, y se navega hacia atrás.
 *   · Cada cifra con su aumento en porcentaje contra el periodo anterior A LA
 *     MISMA ALTURA: un jueves se compara con lunes a jueves de la semana
 *     pasada, no con la semana entera.
 *   · Facturado y cartera no van: saldrán del área de cartera de la app.
 */
const PERIODOS: Periodo[] = ['semana', 'mes', 'año']

type Cifra = (c: CifrasDelRango) => { cantidad: number; valor: number }

/** El aumento, listo para la tarjeta. */
function conAumento(actual: number, anterior: number) {
  const { texto, tendencia } = aumento(actual, anterior)
  return { aumento: texto, tendencia }
}

export function CifrasDelPeriodo() {
  const [periodo, setPeriodo] = useState<Periodo>('semana')
  // Cualquier día de adentro: la base devuelve los bordes.
  const [fecha, setFecha] = useState(hoy())
  const { data, isLoading, error } = useElPeriodo(periodo, fecha, fecha >= hoy())

  const p = data && data.periodo === periodo ? data : undefined
  const a = p?.actual
  const b = p?.anterior

  const decididas = a ? a.aceptadas.cantidad + a.rechazadas.cantidad : 0

  // «vs. 21 – 24 sept: 3 · $3,08 M». Que es a la misma altura lo dice el
  // subtítulo una vez; repetido en cada celda partía el renglón.
  const comparacion = (cifra: Cifra, conValor = true) =>
    p && b ? (
      <>
        vs. {rango(b.desde, b.hasta)}:{' '}
        <b>
          {cifra(b).cantidad}
          {conValor ? ` · ${abreviado(Number(cifra(b).valor))}` : ''}
        </b>
      </>
    ) : null

  const serie = (cifra: Cifra) => (p ? p.tendencia.map((c) => cifra(c).cantidad) : [])

  return (
    <section className={estilos.seccion} aria-label="Cifras del periodo">
      {/* EL PANEL MORADO, como «Dónde está el equipo» (Johan: «le falta a La
          semana unas cards moradas como el dónde está el equipo»): el mismo
          fondo de marca, las cifras en tarjetas translúcidas con su ícono en
          un círculo de color, y al pie lo que sigue en juego. */}
      <div className={estilos.panel}>
        <header className={estilos.cabecera}>
          <p className={estilos.rotulo}>
            <span className={estilos.insignia}>
              <IconoCalendario />
            </span>
            <span>
              {EL_PERIODO[periodo]}
              <small>
                {periodo === 'semana' ? 'lunes a domingo · ' : ''}valores con IVA · cotizaciones de
                la matriz, sin materiales · el aumento es contra {EL_ANTERIOR[periodo]}
                {p?.en_curso ? ' a la misma altura' : ''}
              </small>
            </span>
          </p>

          <div className={estilos.controles}>
            <div className={estilos.selector} role="group" aria-label="Periodo">
              {PERIODOS.map((x) => (
                <button
                  key={x}
                  type="button"
                  className={x === periodo ? estilos.selectorVivo : undefined}
                  aria-pressed={x === periodo}
                  onClick={() => {
                    setPeriodo(x)
                    setFecha(hoy())
                  }}
                >
                  {x === 'semana' ? 'Semana' : x === 'mes' ? 'Mes' : 'Año'}
                </button>
              ))}
            </div>

            <div className={estilos.navegador} role="group" aria-label="Elegir periodo">
              <button
                type="button"
                className={estilos.flecha}
                onClick={() => p && setFecha(correrDia(p.desde, -1))}
                disabled={!p}
                aria-label="Periodo anterior"
              >
                ‹
              </button>
              <span className={estilos.rango}>
                {p ? nombreDelPeriodo(periodo, p.desde, p.hasta) : '…'}
                {p?.en_curso && <span className={estilos.enCurso}>en curso</span>}
              </span>
              <button
                type="button"
                className={estilos.flecha}
                onClick={() => p && setFecha(correrDia(p.hasta, 1))}
                disabled={!p || p.en_curso}
                aria-label="Periodo siguiente"
              >
                ›
              </button>
              {p && !p.en_curso && (
                <button type="button" className={estilos.volver} onClick={() => setFecha(hoy())}>
                  Hoy
                </button>
              )}
            </div>
          </div>
        </header>

        {error ? (
          <p className={estilos.panelVacio}>{(error as Error).message}</p>
        ) : isLoading || !p || !a || !b ? (
          <p className={estilos.panelVacio}>Sacando las cuentas…</p>
        ) : (
          <>
            {/* ── Las cuatro cifras ──────────────────────────────────────── */}
            <div className={estilos.marcador}>
              <TarjetaDeCifra
                tono="oro"
                icono={<IconoFinalizado />}
                rotulo="Servicios finalizados"
                valor={String(a.finalizados.cantidad)}
                detalle={
                  <>
                    por <b>{abreviado(Number(a.finalizados.valor))}</b> · lo aprobado
                  </>
                }
                {...conAumento(a.finalizados.cantidad, b.finalizados.cantidad)}
                serie={serie((c) => c.finalizados)}
                comparacion={comparacion((c) => c.finalizados)}
              />
              <TarjetaDeCifra
                tono="verde"
                icono={<IconoAceptada />}
                rotulo="Aceptadas"
                valor={String(a.aceptadas.cantidad)}
                detalle={
                  <>
                    por <b>{abreviado(Number(a.aceptadas.valor))}</b>
                    {decididas > 0 && (
                      <>
                        {' '}
                        · tasa{' '}
                        <b>{porcentaje(Math.round((a.aceptadas.cantidad / decididas) * 100))}</b>
                      </>
                    )}
                  </>
                }
                {...conAumento(a.aceptadas.cantidad, b.aceptadas.cantidad)}
                serie={serie((c) => c.aceptadas)}
                comparacion={comparacion((c) => c.aceptadas)}
              />
              <TarjetaDeCifra
                tono="rojo"
                icono={<IconoRechazada />}
                rotulo="Rechazadas"
                valor={String(a.rechazadas.cantidad)}
                detalle={
                  <>
                    por <b>{abreviado(Number(a.rechazadas.valor))}</b>
                  </>
                }
                {...conAumento(a.rechazadas.cantidad, b.rechazadas.cantidad)}
                subirEsMalo
                serie={serie((c) => c.rechazadas)}
                comparacion={comparacion((c) => c.rechazadas)}
              />
              <TarjetaDeCifra
                tono="azul"
                icono={<IconoHecha />}
                rotulo="Cotizaciones hechas"
                valor={String(a.cotizadas.cantidad)}
                detalle={
                  <>
                    por <b>{abreviado(Number(a.cotizadas.valor))}</b>
                  </>
                }
                {...conAumento(a.cotizadas.cantidad, b.cotizadas.cantidad)}
                serie={serie((c) => c.cotizadas)}
                comparacion={comparacion((c) => c.cotizadas)}
              />
            </div>

            {/* ── Al pie: lo que sigue en juego y lo que entró ─────────── */}
            <div className={estilos.panelPie}>
              <p>
                <b>{p.esperando_cliente.cantidad}</b>{' '}
                {p.esperando_cliente.cantidad === 1
                  ? 'cotización espera respuesta del cliente'
                  : 'cotizaciones esperan respuesta del cliente'}{' '}
                · {abreviado(Number(p.esperando_cliente.valor))}
              </p>
              <p>
                <b>{a.ordenes_nuevas.cantidad}</b>{' '}
                {a.ordenes_nuevas.cantidad === 1 ? 'orden nueva' : 'órdenes nuevas'} ·{' '}
                {a.ordenes_nuevas.en_campo}{' '}
                {a.ordenes_nuevas.en_campo === 1 ? 'levantada' : 'levantadas'} en la calle
              </p>
            </div>
          </>
        )}
      </div>

      {!error && p && a && b && (
        <>
          {/* ── Lo aceptado, tramo por tramo, y la cartera a futuro ─────── */}
          <div className={estilos.dosBloques}>
            <div className={estilos.bloque}>
              <p className={estilos.bloqueTitulo}>
                Aceptado, {periodo === 'año' ? 'mes a mes' : 'día a día'}
                <span className={estilos.bloqueNota}>
                  las barras de cada cifra son las últimas {cuantos(periodo, p.tendencia.length)}
                </span>
              </p>
              <GraficaDeTramos tramos={p.tramos} periodo={periodo} hoy={p.hoy} />
            </div>
            {/* Cartera recaudada: ya con datos del área de cartera (0247). */}
            <CarteraDelPeriodo periodo={p} />
          </div>

          {/* ── Quién produjo qué, y por qué se perdió lo que se perdió ──── */}
          <div className={estilos.dosBloques}>
            <div className={estilos.bloque}>
              <p className={estilos.bloqueTitulo}>Productividad por técnico</p>
              <PorTecnico filas={p.por_tecnico} />
            </div>
            <div className={estilos.bloque}>
              <p className={estilos.bloqueTitulo}>Por qué se rechazaron</p>
              <Rechazos rechazos={p.rechazos} />
            </div>
          </div>
        </>
      )}
    </section>
  )
}

// ── Los íconos, del mismo trazo que «Dónde está el equipo» ──────────────────
function Trazo({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

const IconoCalendario = () => (
  <Trazo>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </Trazo>
)
const IconoFinalizado = () => (
  <Trazo>
    <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
  </Trazo>
)
const IconoAceptada = () => (
  <Trazo>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.5 12.5 2.4 2.4 4.6-5" />
  </Trazo>
)
const IconoRechazada = () => (
  <Trazo>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m9.2 9.2 5.6 5.6M14.8 9.2l-5.6 5.6" />
  </Trazo>
)
const IconoHecha = () => (
  <Trazo>
    <path d="M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8z" />
    <path d="M14 3.5V8h4.5M9 13h6M9 16.5h4" />
  </Trazo>
)
